import React from "react";
import { motion } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { fmtUSD } from "../../lib/format";
import { track } from "../../lib/track";

export default function NetProceedsSnapshot({ result, inputs }) {
  const t = result?.totals;
  if (!t) return null;

  const price = Number(inputs?.sale_price) || 0;
  const listingFee = price * 0.01;
  const buyerFee = price * 0.025;
  // Baseline closing = live transfer taxes + escrow + title (city-accurate, reactive via Step 5).
  const baselineClosing = (t.seller_transfer_taxes || 0) + (t.seller_escrow || 0) + (Number(inputs?.title_insurance) || 0);
  const net = price - listingFee - buyerFee - baselineClosing;

  const goToStep5 = () => {
    track("net_sheet_customize_click", { net });
    const el = document.getElementById("step-5-seller-net-sheet");
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    el.classList.add("ring-4", "ring-brand-gold", "ring-offset-4", "rounded-2xl", "transition");
    setTimeout(() => el.classList.remove("ring-4", "ring-brand-gold", "ring-offset-4", "rounded-2xl", "transition"), 2200);
  };

  return (
    <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
      className="rounded-2xl border border-brand-cream/10 bg-brand-emerald text-brand-cream p-6 sm:p-8" data-testid="net-proceeds-snapshot">
      <span className="eyebrow text-brand-gold">Estimated Net Proceeds Snapshot</span>
      <h2 className="mt-2 font-serif text-2xl sm:text-3xl font-light text-brand-cream">A quick look at what you walk away with using Equitas at 1%</h2>

      <div className="mt-6 space-y-2.5">
        <Row label="Expected sale price" value={price} positive testid="snapshot-sale-price" />
        <Row label="Equitas listing fee (1%)" value={-listingFee} testid="snapshot-listing-fee" />
        <Row label="Est. buyer-broker compensation (2.5% default)" value={-buyerFee} testid="snapshot-buyer-fee" />
        <Row label="Est. county/city taxes & closing costs" value={-baselineClosing} testid="snapshot-closing-costs" />
      </div>

      <div className="mt-5 flex items-baseline justify-between border-t border-brand-cream/15 pt-4">
        <span className="text-sm font-semibold text-brand-cream">Estimated Net Proceeds</span>
        <span className="font-mono text-3xl font-bold text-brand-gold" data-testid="snapshot-net-proceeds">{fmtUSD(net)}</span>
      </div>

      <div className="mt-6 rounded-xl bg-brand-dark/50 p-4">
        <p className="text-xs text-brand-cream/70 leading-relaxed">
          Have a mortgage payoff, HOA fees, seller credits, or custom tax splits? Complete Step 5 below to generate your fully customized, line-by-line net sheet.
        </p>
        <button onClick={goToStep5} data-testid="snapshot-goto-step5"
          className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-brand-cream px-4 py-2 text-sm font-semibold text-brand-emerald shadow-sm transition-colors hover:bg-white">
          Customize &amp; Refine Net Sheet (Step 5) <ArrowDown className="h-4 w-4" />
        </button>
      </div>

      <p className="mt-4 text-xs text-brand-cream/50 leading-relaxed">
        Illustrative snapshot using a 1% Equitas listing fee and a standard 2.5% buyer-broker compensation. Commissions and fees are fully negotiable. Excludes mortgage payoff, HOA, seller credits and other custom items — add those in Step 5.
      </p>
    </motion.div>
  );
}

const Row = ({ label, value, positive, testid }) => (
  <div className="flex items-baseline justify-between text-sm" data-testid={testid}>
    <span className="text-brand-cream/70">{label}</span>
    <span className={`font-mono ${positive ? "text-brand-cream font-semibold" : "text-brand-cream"}`}>
      {value < 0 ? `−${fmtUSD(Math.abs(value))}` : fmtUSD(value)}
    </span>
  </div>
);
